
import { Component, ChangeDetectionStrategy, inject, signal, OnInit, computed } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { CommonModule } from '@angular/common';
import { Store } from '../../models';

@Component({
  selector: 'app-onboarding',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './onboarding.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingComponent implements OnInit {
  private authService = inject(AuthService);
  private router: Router = inject(Router);
  private fb: FormBuilder = inject(FormBuilder);

  onboardForm: FormGroup;
  isSubmitting = signal(false);
  errorMessage = signal<string | null>(null);
  
  currentStore = this.authService.currentStore;

  constructor() {
    this.onboardForm = this.fb.group({
      storeName: ['', [Validators.required, Validators.minLength(3)]],
      ownerPin: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });
  }

  ngOnInit() {
    const store = this.authService.currentStore();
    
    // Security check: If already setup, go to dashboard
    if (store && store.setupCompleted) {
        this.router.navigate(['/admin/dashboard']);
        return;
    }
  }

  async onSubmit() {
    if (this.onboardForm.invalid) return;

    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    const { storeName, ownerPin } = this.onboardForm.value;
    const supabase = this.authService.getSupabaseClient();

    if (!supabase) {
        this.errorMessage.set("Database connection failed.");
        this.isSubmitting.set(false);
        return;
    }

    try {
        const { error } = await supabase.rpc('complete_store_setup', {
            p_store_name: storeName,
            p_owner_pin: ownerPin
        });

        if (error) throw error;

        // Force reload profile to update local state (setupCompleted flag)
        await this.authService.reloadCurrentUserProfile();
        
        // Success
        this.router.navigate(['/admin/dashboard']);

    } catch (e: any) {
        console.error("Onboarding Error:", e);
        if (e.code === '23505' && (e.message?.includes('pin') || e.details?.includes('pin'))) {
            this.errorMessage.set("รหัส PIN นี้ถูกใช้งานแล้วในร้านนี้ (PIN already exists in this store)");
        } else {
            this.errorMessage.set(e.message || "An error occurred during setup.");
        }
    } finally {
        this.isSubmitting.set(false);
    }
  }
}
